import React, { useState } from 'react';

import { SerializedStyles } from '@emotion/react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { InputBase } from '@mui/material';
import Autocomplete, { AutocompleteCloseReason } from '@mui/material/Autocomplete';
import ButtonBase from '@mui/material/ButtonBase';
import Popper, { PopperPlacementType } from '@mui/material/Popper';
import { alpha, Theme } from '@mui/material/styles';

import styled, { css } from '../../styled';
import { colorsPalette } from '../../theme';

type SelectProps = {
  className?: string;
  id?: string;
  labelId?: string;
  value?: any | any[];
  onChange?: (any) => void;
  items?: SelectItem[];
  multiple?: boolean;
  anchorRef?: React.RefObject<HTMLElement>;
  widthByAnchor?: boolean;
  search?: boolean;
  popperLabel?: string;
  placement?: PopperPlacementType;
  wrapperCss?: SerializedStyles | (({ theme }: { theme: Theme }) => SerializedStyles);
  selectedItemCss?: SerializedStyles | (({ theme }: { theme: Theme }) => SerializedStyles);
  selectHeaderCss?: SerializedStyles | (({ theme }: { theme: Theme }) => SerializedStyles);
  selectBtnCss?: SerializedStyles | (({ theme }: { theme: Theme }) => SerializedStyles);
};

type SelectItem = {
  value: string | number;
  display?: string | null;
};

const Input = (params) => {
  const className = params?.className;

  return (
    <InputBase
      className={className}
      ref={params.InputProps.ref}
      inputProps={params.inputProps}
      autoFocus
    />
  );
};

const Select: React.FC<SelectProps> = ({
  className,
  id,
  value,
  onChange,
  items = [],
  multiple = false,
  anchorRef = null,
  widthByAnchor = false,
  search = false,
  popperLabel = '',
  placement = 'bottom-start',
  wrapperCss,
  selectedItemCss,
  selectHeaderCss,
  selectBtnCss,
  ...rest
}) => {
  const anchorEl = anchorRef?.current;
  const width = widthByAnchor && anchorEl?.clientWidth;
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const handleClick = () => setOpen(true);

  // eslint-disable-next-line
  const handleClose = (event: React.ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
    if (reason === 'toggleInput') {
      return;
    }

    setOpen(false);
  };

  const getOptionSelected = (option, value) => option?.value === value?.value;

  const renderOption = (props, option, { selected }) => {
    return (
      <OptionInner {...props} selected={selected}>
        <div>{option.display}</div>
        <VisibilityIcon
          style={{ visibility: selected ? 'visible' : 'hidden', color: colorsPalette.gray }}
        />
      </OptionInner>
    );
  };

  // eslint-disable-next-line
  const handleInputChange = (event, value, reason) => {
    if (!search) return;

    if (reason === 'reset') {
      setInputValue('');
      return;
    }
    setInputValue(value);
  };

  // eslint-disable-next-line
  const handleOnChange = (event, value, reason) => {
    // @ts-ignore
    onChange(value);
    if (!multiple) {
      setOpen(false);
    }
  };

  return (
    <Wrapper className={className} optionalStyles={wrapperCss}>
      <div>
        <StyledButtonBase
          data-testid={rest['data-testid'] + 'Title'}
          optionalStyles={selectBtnCss}
          disableRipple
          aria-describedby={id}
          onClick={handleClick}
        >
          {multiple ? (
            <SelectHeader optionalStyles={selectHeaderCss}>
              <SelectedItem optionalStyles={selectedItemCss}>
                {value.map((item, index) => {
                  const isLast = value.length - 1 === index;

                  return (
                    <StyledSelectItem key={item?.value}>
                      {item?.display}
                      {isLast ? '' : ', '}
                    </StyledSelectItem>
                  );
                })}
              </SelectedItem>

              <ChevronIcon fontSize="large" />
            </SelectHeader>
          ) : (
            <SelectHeader optionalStyles={selectHeaderCss}>
              <StyledSelectItem>{value?.display}</StyledSelectItem>

              <ChevronIcon fontSize="large" />
            </SelectHeader>
          )}
        </StyledButtonBase>
      </div>
      {open && <FiltersOverlay />}
      <StyledPopper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        anchorWidth={width}
        disablePortal
        modifiers={[
          {
            name: 'flip',
            enabled: false,
          },
          {
            name: 'offset',
            options: {
              offset: [0, -45],
            },
          },
        ]}
      >
        <div>
          <PopoverHeader>
            <StyledSelectItem>{popperLabel}</StyledSelectItem>
            <ChevronIcon fontSize="large" />
          </PopoverHeader>
        </div>
        <StyledAutocomplete
          data-testid={rest['data-testid']}
          open={open}
          onClose={handleClose}
          multiple={multiple}
          value={value}
          onChange={handleOnChange}
          disableCloseOnSelect
          fullWidth
          disablePortal
          inputValue={inputValue}
          onInputChange={handleInputChange}
          renderTags={() => null}
          noOptionsText="No options"
          isOptionEqualToValue={getOptionSelected}
          renderOption={renderOption}
          options={items}
          getOptionLabel={(option) => option.display || ''}
          renderInput={(params) => (
            <StyledInput optionalStyles={search ? undefined : inputHidedCss} {...params} />
          )}
        />
      </StyledPopper>
    </Wrapper>
  );
};

export default Select;

const Wrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  ${({ optionalStyles }) => optionalStyles}
`;
const SelectedItem = styled('div', {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  max-width: 30px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  ${({ theme }) => theme.breakpoints.up('md')} {
    max-width: 180px;
  }
  ${({ optionalStyles }) => optionalStyles}
`;
const StyledButtonBase = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  ${({ optionalStyles }) => optionalStyles}
`;
const SelectHeader = styled('div', {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  display: flex;
  align-items: center;
  ${({ optionalStyles }) => optionalStyles}
`;
const OptionInner = styled('div', {
  shouldForwardProp: (prop) => prop !== 'selected',
})`
  && {
    justify-content: space-between;
    padding: 11px;
    width: 100%;
    display: flex;
    align-items: center;
    border-radius: 5px;
    cursor: pointer;
    color: ${colorsPalette.gray};
    &[aria-selected='true'],
    &[aria-selected='true'].Mui-focused,
    &[aria-selected='true']:hover {
      ${({ selected }) => selected && `background-color: ${colorsPalette.blue};`}
    }
  }
`;
const StyledSelectItem = styled('span')`
  font-size: 12px;
  font-weight: bold;
  color: ${colorsPalette.gray};
  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 16px;
  }
`;
const ChevronIcon = styled(KeyboardArrowDownIcon)`
  fill: ${colorsPalette.blue};
`;
const FiltersOverlay = styled('div')`
  position: fixed;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background: ${alpha(colorsPalette.blueDark, 0.8)};
  z-index: 1;
`;
const StyledPopper = styled(Popper, {
  shouldForwardProp: (prop) => prop !== 'anchorWidth',
})`
  padding: 20px;
  background: ${colorsPalette.violetAnalyticsCard};
  border-radius: 20px;
  box-shadow: 0px 3px 6px ${alpha(colorsPalette.blackPure, 0.4)};
  width: ${({ anchorWidth }) => anchorWidth + 'px' || 'unset'};
  z-index: 2;
`;
const PopoverHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  & > span {
    font-size: 18px;
  }
`;
const StyledAutocomplete = styled(Autocomplete)`
  & ~ .MuiAutocomplete-popper .MuiAutocomplete-paper {
    margin: 0;
    background: ${colorsPalette.violetAnalyticsCard};
    border-radius: 0;
    box-shadow: none;
  }
  & ~ .MuiAutocomplete-popper .MuiAutocomplete-option {
    border-radius: 5px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  & ~ .MuiAutocomplete-popper.MuiAutocomplete-popperDisablePortal {
    transform: translate(0, 0) !important;
    position: static !important;
    width: 100% !important;
  }
`;
const StyledInput = styled(Input, {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  background: ${alpha(colorsPalette.white, 0.1)};
  border-radius: 50px;
  padding: 0 15px;
  height: 45px;
  width: 100%;
  margin-bottom: 20px;
  && {
    ${({ optionalStyles }) => optionalStyles}
  }
`;
const inputHidedCss = css`
  height: 0;
  padding: 0;
  margin-bottom: 0;
  opacity: 0;
  margin-top: -20px;
  & input {
    cursor: default;
  }
`;
